import React from "react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { convertDisplayToDb, formatCurrency } from "@/utils/currency";
import type { PaymentValidationErrors } from "../types";

interface TaxBreakdownCardProps {
  baseFee: string;
  includeGst: boolean;
  onIncludeGstChange: (checked: boolean) => void;
  gstPercentage: string;
  onGstPercentageChange: (val: string) => void;
  gstAmount: number;
  includeTds: boolean;
  onIncludeTdsChange: (checked: boolean) => void;
  tdsPercentage: string;
  onTdsPercentageChange: (val: string) => void;
  tdsAmount: number;
  netPayable: number;
  userCountry?: string | null;
  errors: PaymentValidationErrors;
}

export const TaxBreakdownCard: React.FC<TaxBreakdownCardProps> = ({
  baseFee,
  includeGst,
  onIncludeGstChange,
  gstPercentage,
  onGstPercentageChange,
  gstAmount,
  includeTds,
  onIncludeTdsChange,
  tdsPercentage,
  onTdsPercentageChange,
  tdsAmount,
  netPayable,
  userCountry,
  errors,
}) => {
  return (
    <>
      {/* GST Section */}
      <div className="space-y-3 p-4 border rounded-lg bg-gray-50">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="includeGst"
            checked={includeGst}
            onCheckedChange={(checked) => onIncludeGstChange(checked === true)}
          />
          <label htmlFor="includeGst" className="font-medium">
            Apply GST?
          </label>
        </div>

        {includeGst && (
          <div className="space-y-3">
            <div>
              <label className="block font-medium mb-1">
                GST Percentage (%)
              </label>
              <Input
                type="number"
                min={0}
                step="0.1"
                placeholder="18.0"
                value={gstPercentage}
                onChange={(e) => onGstPercentageChange(e.target.value)}
              />
              {errors.gstPercentage && (
                <p className="text-destructive text-xs mt-1">
                  {errors.gstPercentage}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* TDS Section */}
      <div className="space-y-3 p-4 border rounded-lg bg-orange-50">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="includeTds"
            checked={includeTds}
            onCheckedChange={(checked) => onIncludeTdsChange(checked === true)}
          />
          <label htmlFor="includeTds" className="font-medium">
            Deduct TDS?
          </label>
        </div>

        {includeTds && (
          <div className="space-y-3">
            <div>
              <label className="block font-medium mb-1">
                TDS Percentage (%)
              </label>
              <Input
                type="number"
                min={0}
                step="0.1"
                placeholder="10.0"
                value={tdsPercentage}
                onChange={(e) => onTdsPercentageChange(e.target.value)}
              />
              {errors.tdsPercentage && (
                <p className="text-destructive text-xs mt-1">
                  {errors.tdsPercentage}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Amount Breakdown */}
      {baseFee && (
        <div className="space-y-2 p-4 border rounded-lg bg-blue-50">
          <h4 className="font-semibold text-sm">Payment Breakdown</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>Base Fee:</span>
              <span>
                {formatCurrency(
                  convertDisplayToDb(parseFloat(baseFee) || 0, userCountry),
                  userCountry
                )}
              </span>
            </div>
            {includeGst && (
              <div className="flex justify-between text-green-700">
                <span>+ GST ({gstPercentage}%):</span>
                <span>
                  {formatCurrency(
                    convertDisplayToDb(gstAmount, userCountry),
                    userCountry
                  )}
                </span>
              </div>
            )}
            {includeTds && (
              <div className="flex justify-between text-red-700">
                <span>- TDS ({tdsPercentage}%):</span>
                <span>
                  {formatCurrency(
                    convertDisplayToDb(tdsAmount, userCountry),
                    userCountry
                  )}
                </span>
              </div>
            )}
            <div className="flex justify-between font-semibold border-t pt-2 text-lg">
              <span>Net Payable Amount:</span>
              <span>
                {formatCurrency(
                  convertDisplayToDb(netPayable, userCountry),
                  userCountry
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
